import type { SeriesChartSeries } from '../../components/charts/SeriesChart';

/**
 * E43 · the phone's rows on E37's Check-ins tab and session recap (Q-e43g1-6 A) — the fields the two queries add, and
 * the pure shaping the screens draw. Numbers only: no labels, thresholds or verdicts.
 */
export type CheckInSleepNightRow = {
  night: string;
  wakeDay: string;
  inBedStart: string;
  inBedEnd: string;
  asleepMinutes: number;
  efficiency?: number | null;
};
export type CheckInActivityDayRow = { date: string; activeMinutes: number };
export type CheckInSharedTypesRow = { sleep: boolean; activity: boolean };
export type CheckInPhoneAveragesRow = {
  asleepMinutes?: number | null;
  efficiency?: number | null;
  activeMinutes?: number | null;
};

const SLEEP_NIGHT_FIELDS = ['night', 'wakeDay', 'inBedStart', 'inBedEnd', 'asleepMinutes', 'efficiency'];

/** ClientCheckInTrend — the E43 fields. */
export const TREND_PHONE_FIELDS = [
  { sharedTypes: ['sleep', 'activity'] },
  { sleepNights: SLEEP_NIGHT_FIELDS },
  { activityDays: ['date', 'activeMinutes'] },
];

/** CheckInsSinceLastSession — the E43 fields. */
export const RECAP_PHONE_FIELDS = [
  { sharedTypes: ['sleep', 'activity'] },
  { phoneAverages: ['asleepMinutes', 'efficiency', 'activeMinutes'] },
];

export type TrendPhone = {
  sharedTypes?: CheckInSharedTypesRow | null;
  sleepNights?: CheckInSleepNightRow[] | null;
  activityDays?: CheckInActivityDayRow[] | null;
};

export type RecapPhone = {
  sharedTypes?: CheckInSharedTypesRow | null;
  phoneAverages?: CheckInPhoneAveragesRow | null;
};

const byX = (a: { x: string }, b: { x: string }) => (a.x < b.x ? -1 : 1);

/** Hours asleep per wake day — the line under the mood line (one point a day, the mood chart's noon). */
export const asleepSeriesOf = (nights: readonly CheckInSleepNightRow[]): SeriesChartSeries[] => [
  {
    key: 'asleep',
    label: 'asleep',
    points: nights
      .map((n) => ({ x: `${n.wakeDay}T12:00:00`, y: Math.round((n.asleepMinutes / 60) * 10) / 10 }))
      .sort(byX),
  },
];

/** Active minutes per day. */
export const activeSeriesOf = (days: readonly CheckInActivityDayRow[]): SeriesChartSeries[] => [
  {
    key: 'active',
    label: 'active',
    points: days.map((d) => ({ x: `${d.date}T12:00:00`, y: d.activeMinutes })).sort(byX),
  },
];

const mean = (xs: number[]): number | null =>
  xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null;

/** The tab's caption for each row: the averages over the window. */
export const trendAverages = (phone: TrendPhone): CheckInPhoneAveragesRow => ({
  asleepMinutes: mean((phone.sleepNights ?? []).map((n) => n.asleepMinutes)),
  efficiency: mean(
    (phone.sleepNights ?? []).map((n) => n.efficiency).filter((x): x is number => typeof x === 'number'),
  ),
  activeMinutes: mean((phone.activityDays ?? []).map((d) => d.activeMinutes)),
});

/** `6 h 42 min` parts for the duration copy. */
export const hoursAndMinutes = (minutes: number) => ({ hours: Math.floor(minutes / 60), minutes: minutes % 60 });
